import { Link } from "react-router-dom"
import { api } from "../../api/client"
import { cx } from "../../components/ui"
import { Logo, LoominaMark } from "../../components/brand/Logo"

/** The shell every signed-out screen sits in. */
export function AuthLayout({ title, description, children, footer, wide = false }) {
	return (
		<div className="flex min-h-screen flex-col bg-ink-50">
			<header className="px-5 py-5 sm:px-8">
				<Link to="/" aria-label="Loomina" className="inline-flex text-ink-900">
					<Logo size="sm" />
				</Link>
			</header>

			<main className="flex flex-1 items-start justify-center px-4 pb-16 pt-4 sm:px-6">
				<div className={cx("w-full", wide ? "max-w-xl" : "max-w-sm")}>
					<div className="rounded-xl border border-ink-200/80 bg-white p-6 shadow-card sm:p-7">
						<h1 className="text-lg font-semibold tracking-tight text-ink-900">
							{title}
						</h1>
						{description && (
							<p className="mt-1 text-[0.8125rem] leading-relaxed text-ink-500">
								{description}
							</p>
						)}
						<div className="mt-5">{children}</div>
					</div>
					{footer && (
						<p className="mt-4 text-center text-[0.8125rem] text-ink-500">{footer}</p>
					)}
				</div>
			</main>
		</div>
	)
}

/** Kept so older imports of this name keep resolving. */
export function Logomark({ size = "md", className }) {
	return <LoominaMark size={size === "lg" ? 40 : 28} className={className} />
}

/**
 * Social sign-in, below the form.
 *
 * Below rather than above, because email and password is the primary route and
 * the provider buttons are the alternative - putting them first makes the form
 * underneath look like an afterthought. Side by side, because two equal options
 * on one row read as a choice; stacked full-width they read as a queue.
 *
 * Renders nothing at all when no provider is configured, so the divider never
 * appears over an empty row.
 */
export function OAuthButtons({ providers, next = "/", disabled = false }) {
	if (!providers?.length) return null

	return (
		<div className="mt-5">
			<div className="flex items-center gap-3">
				<span className="h-px flex-1 bg-ink-200" />
				<span className="text-[0.8125rem] text-ink-500">Or continue with</span>
				<span className="h-px flex-1 bg-ink-200" />
			</div>

			<div
				className={cx(
					"mt-4 grid gap-2.5",
					providers.length > 1 ? "grid-cols-2" : "grid-cols-1"
				)}
			>
				{providers.map((provider) => (
					<a
						key={provider.provider}
						// Through the API client, so this points at whatever
						// VITE_API_BASE says. Hardcoding "/api" worked in dev, where
						// Vite proxies it, and silently broke in production, where the
						// API is on its own host: the browser asked Cloudflare for
						// /api/auth/oauth/google/start, got the SPA fallback, and the
						// router bounced the visitor to /register.
						href={disabled ? undefined : api.oauthStartUrl(provider.provider, next)}
						className={cx(
							"flex h-11 items-center justify-center gap-2.5 rounded-lg border border-ink-200",
							"bg-white text-sm font-medium text-ink-800 transition-colors",
							"hover:border-ink-300 hover:bg-ink-50",
							disabled && "pointer-events-none opacity-50"
						)}
					>
						<ProviderMark provider={provider.provider} />
						{provider.label}
					</a>
				))}
			</div>
		</div>
	)
}

function ProviderMark({ provider }) {
	if (provider === "google") {
		return (
			<svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
				<path
					fill="#4285F4"
					d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
				/>
				<path
					fill="#34A853"
					d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.93v2.33A9 9 0 0 0 9 18Z"
				/>
				<path
					fill="#FBBC05"
					d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.93a9 9 0 0 0 0 8.1l3.04-2.33Z"
				/>
				<path
					fill="#EA4335"
					d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .93 4.95l3.04 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
				/>
			</svg>
		)
	}
	return (
		<svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
			<path fill="#F25022" d="M0 0h8.5v8.5H0z" />
			<path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
			<path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
			<path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
		</svg>
	)
}
